/* eslint-disable no-underscore-dangle */
import * as yup from "yup";
import { ComparisonRef, getRefLabel, isRef } from "utils/schemas/utils";
import { formatDecimal } from "utils/formatting";

type EmptyObj = Record<string, never>;

type ComparisonExtraParams = {
  comparisonRef?: ComparisonRef;
  comparisonValue: Decimal;
  getComparisonLabel: () => string | undefined;
  getFormattedComparisonValue: (value: Decimal) => string;
};

type ComparisonTestOptions = {
  message?: yup.Message<ComparisonExtraParams>;
  isExclusive?: boolean;
};

function makeGetComparisonMessage(
  comparisonDescription: string,
): yup.Message<ComparisonExtraParams> {
  return ({
    label,
    comparisonValue,
    getComparisonLabel,
    getFormattedComparisonValue,
  }) => {
    const formattedComparisonValue =
      getFormattedComparisonValue(comparisonValue);
    const comparisonLabel = getComparisonLabel();
    return `${label ? `${label} m` : "M"}ust be ${comparisonDescription} ${
      comparisonLabel
        ? `${comparisonLabel} (${formattedComparisonValue})`
        : formattedComparisonValue
    }.`;
  };
}

function ensureNumber(value: Decimal | number) {
  if (typeof value === "string") return parseFloat(value);
  return value;
}

type Options = {
  numberFormatter?: (value: number | Decimal) => string | undefined;
};

class DecimalStringSchema<
  TType extends yup.Maybe<Decimal> = Decimal | undefined,
  TContext = yup.AnyObject,
  TDefault = undefined,
  TFlags extends yup.Flags = "",
> extends yup.Schema<TType, TContext, TDefault, TFlags> {
  private options: Options | undefined;

  private _nestedFromCache: Partial<{ [key: string]: yup.ISchema<unknown> }> =
    {};

  constructor(options?: Options) {
    super({
      type: "decimal-string",
      check: (value: unknown): value is NonNullable<TType> => {
        if (typeof value !== "string") return false;
        if (!value) return false;
        if (!/^-?\d*(?:\.\d*)?$/.test(value.replace(/,/g, ""))) return false;
        return !Number.isNaN(parseFloat(value));
      },
    });

    this.withMutation(() => {
      this.transform(
        (
          value: Decimal | number | null | undefined,
          originalValue,
          context,
        ) => {
          if (!context.spec.coerce || typeof value === "string") return value;
          if (typeof value === "number") return String(value);
          return value;
        },
      );
    });

    this.options = options;
  }

  clone(spec?: Parameters<yup.Schema["clone"]>[0]): this {
    const next = super.clone(spec);
    next.options = this.options;
    return next;
  }

  private prepareParam(
    ref: unknown,
    name: string,
  ): number | Decimal | yup.Reference<number | Decimal> {
    if (isRef(ref)) {
      return ref as yup.Reference<number | Decimal>;
    }

    const cast = this.cast(ref);
    if (!this._typeCheck(cast))
      throw new TypeError(
        `\`${name}\` must be a number or a value that can be \`cast()\` to a number`,
      );
    return cast;
  }

  private formatNumber(value: number | Decimal) {
    if (this.options?.numberFormatter)
      return this.options.numberFormatter(value) || String(value);
    return formatDecimal(value);
  }

  private useFromSchemaCache(key: string) {
    return {
      getFromSchema: () => this._nestedFromCache?.[key],
      setWithContext: (context: yup.TestContext<TContext>) => {
        if (!this._nestedFromCache) this._nestedFromCache = {};
        this._nestedFromCache[key] = context.from?.[0]?.schema;
      },
    };
  }

  private makeComparisonTest(
    name: string,
    test: (value: number, comparisonValue: number) => boolean,
  ) {
    return (
      comparisonValueOrRef: number | Decimal | yup.Reference<number | Decimal>,
      message: yup.Message<ComparisonExtraParams>,
    ) => {
      const limit = this.prepareParam(comparisonValueOrRef, name);

      const { getFromSchema, setWithContext } = this.useFromSchemaCache(name);

      const comparisonRef = isRef(limit)
        ? { ref: limit, getFromSchema }
        : undefined;

      return this.test({
        message,
        name,
        exclusive: true,
        params: {
          comparisonValue: limit,
          comparisonRef,
          getComparisonLabel: () =>
            comparisonRef ? getRefLabel(comparisonRef) : undefined,
          getFormattedComparisonValue: (v) => this.formatNumber(v),
        } as ComparisonExtraParams,
        skipAbsent: true,
        test(value, context) {
          setWithContext(context);
          const comparisonValue = this.resolve(limit);
          if (!comparisonValue) return true;
          const comparisonNumber = ensureNumber(comparisonValue);
          if (Number.isNaN(comparisonNumber)) return true;
          return test(ensureNumber(value!), comparisonNumber);
        },
      });
    };
  }

  min(
    min: number | Decimal | yup.Reference<number | Decimal>,
    { isExclusive, message }: ComparisonTestOptions = {},
  ) {
    return this.makeComparisonTest("min", (value, comparisonValue) =>
      isExclusive ? value > comparisonValue : value >= comparisonValue,
    )(
      min,
      message ||
        makeGetComparisonMessage(
          `greater than${isExclusive ? "" : " or equal to"}`,
        ),
    );
  }

  max(
    max: number | Decimal | yup.Reference<number | Decimal>,
    { isExclusive, message }: ComparisonTestOptions = {},
  ) {
    return this.makeComparisonTest("max", (value, comparisonValue) =>
      isExclusive ? value < comparisonValue : value <= comparisonValue,
    )(
      max,
      message ||
        makeGetComparisonMessage(
          `less than${isExclusive ? "" : " or equal to"}`,
        ),
    );
  }

  default<D extends yup.Maybe<TType>>(def: yup.DefaultThunk<D, TContext>) {
    return super.default(def) as DecimalStringSchema<
      TType,
      TContext,
      TDefault,
      TFlags
    >;
  }

  defined(message?: yup.Message<EmptyObj> | undefined) {
    return super.defined(message) as DecimalStringSchema<
      yup.Defined<TType>,
      TContext,
      TDefault,
      TFlags
    >;
  }

  required(message?: yup.Message<EmptyObj> | undefined) {
    return super.required(message) as DecimalStringSchema<
      NonNullable<TType>,
      TContext,
      TDefault,
      TFlags
    >;
  }

  notRequired() {
    return super.notRequired() as DecimalStringSchema<
      yup.Maybe<TType>,
      TContext,
      TDefault,
      TFlags
    >;
  }

  nullable() {
    return super.nullable() as DecimalStringSchema<
      TType | null,
      TContext,
      TDefault,
      TFlags
    >;
  }

  nonNullable(message?: yup.Message<EmptyObj> | undefined) {
    return super.nonNullable(message) as DecimalStringSchema<
      yup.NotNull<TType>,
      TContext,
      TDefault,
      TFlags
    >;
  }
}

export default function decimalStr(options?: Options) {
  return new DecimalStringSchema(options);
}

export function definedDecimalStr(options?: Options) {
  return decimalStr(options).nullable().defined().default(null);
}
